import { useMemo } from 'react';
import { Layer, Source } from 'shared/map-exports';
import {
  LocationListItem,
  LocationType,
} from 'features/locations/location.types';
import { FeatureCollection, Feature, Geometry } from 'geojson';
import { useMapLayersState } from 'state/useMapLayersState';
import { MapClickHandler, MapboxFeature } from 'features/mapping/mapping.types';
import { getLocationIcon } from 'features/mapping/icons';
import {
  LOCATIONS_SOURCE_ID,
  LOCATION_ICONS_LAYER_ID,
  LOCATION_LABELS_LAYER_ID,
  ON_TOP_OF_EVERYTHING_LAYER_ID,
} from 'features/mapping/styles/constants';
import { TFunction } from 'i18next';
import {
  LOCATION_HOURS_STATUS_TO_TRANS_KEY,
  getLocationTypeTransKey,
} from 'features/locations/location.constants';
import { useTranslation } from 'react-i18next';
import { useLocationV2Enabled } from 'features/locations/hooks/useLocationV2Enabled';
import { history } from 'shared/history';
import { getLocationStatusForViewer } from 'features/locations/utils/operatingHours';
import {
  getLegacyIconStyle,
  getIconStyle,
  getLegacyLabelStyle,
  getLabelStyle,
} from './LocationsLayer.styles';

type LocationsLayerProps = {
  locations: LocationListItem[];
  selectedGeoEventId?: number;
  interactive?: boolean;
};

type LocationFeatureProperties = {
  id: number;
  name: string;
  icon: string;
  isActive: boolean;
  offsetX: number;
  offsetY: number;
  opacity: number;
  index: number;
  indexInv: number;
};

type LocationFeaturePropertiesV2 = {
  id: number;
  icon: string;
  label: string;
  isActive: boolean;
  offsetX: number;
  offsetY: number;
  opacity: number;
  index: number;
  indexInv: number;
  type: LocationType;
  labelBackgroundColor: string;
  textColor: string;
};

const LOCATION_TYPE_LABEL_BACKGROUND_COLOR = '#FFFFFF';
const LOCATION_TYPE_LABEL_TEXT_COLOR = '#333333';
const AT_CAPACITY_LABEL_BACKGROUND_COLOR = '#E25345';
const AT_CAPACITY_LABEL_TEXT_COLOR = '#FFFFFF';

const toGeojsonFeature = (
  t: TFunction,
  location: LocationListItem,
  showLocationV2: boolean,
): Feature<
  Geometry,
  LocationFeatureProperties | LocationFeaturePropertiesV2
> => {
  const { id, isActive, name, lat, lng, data, dateCreated } = location;
  const icon = getLocationIcon(data.locationType, showLocationV2);
  const opacity = isActive ? 1 : 0;

  // Sort indexes for labels and text. Inactive and Rx fires on the bottom,
  // then oldest to newest.
  const index = isActive ? dateCreated.getTime() : 0;
  const indexInv = 1 / (index + 1);

  if (showLocationV2) {
    let label = data.atCapacity
      ? t('location.atCapacity')
      : t(getLocationTypeTransKey(data.locationType, showLocationV2));

    let labelBackgroundColor = data.atCapacity
      ? AT_CAPACITY_LABEL_BACKGROUND_COLOR
      : LOCATION_TYPE_LABEL_BACKGROUND_COLOR;

    let textColor = data.atCapacity
      ? AT_CAPACITY_LABEL_TEXT_COLOR
      : LOCATION_TYPE_LABEL_TEXT_COLOR;

    // At capacity takes precedence over operating hours
    if (!data.atCapacity && data.hours.length) {
      const operatingHoursStatus = getLocationStatusForViewer(location);

      if (operatingHoursStatus !== 'open') {
        label = t(LOCATION_HOURS_STATUS_TO_TRANS_KEY[operatingHoursStatus]);
        labelBackgroundColor = AT_CAPACITY_LABEL_BACKGROUND_COLOR;
        textColor = AT_CAPACITY_LABEL_TEXT_COLOR;
      }
    }

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [lng, lat],
      },
      properties: {
        id,
        icon: icon.name,
        label,
        isActive,
        offsetX: 0,
        offsetY: 0,
        opacity,
        index,
        indexInv,
        type: data.locationType,
        labelBackgroundColor,
        textColor,
      },
    };
  }

  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [lng, lat],
    },
    properties: {
      id,
      name,
      icon: icon.name,
      isActive,
      offsetX: 0,
      offsetY: 0,
      opacity,
      index,
      indexInv,
    },
  };
};

export const resolveLocationPath = (
  geoJsonFeatures: MapboxFeature<LocationFeatureProperties>[],
): string | null => {
  const feature = geoJsonFeatures[0];
  const { id, opacity } = feature.properties;
  if (opacity > 0) return `/location/${id}`;
  return null;
};

export const locationClickHandler: MapClickHandler<
  LocationFeatureProperties
> = (geoJsonFeatures) => {
  const path = resolveLocationPath(geoJsonFeatures);
  if (path) history.push(path);
};

export const LocationsLayer = (props: LocationsLayerProps): JSX.Element => {
  const { locations, selectedGeoEventId = 0, interactive = true } = props;
  const { isLightBaseLayer } = useMapLayersState();
  const { t } = useTranslation();
  const showLocationV2 = useLocationV2Enabled();

  const geojson: FeatureCollection<
    Geometry,
    LocationFeatureProperties | LocationFeaturePropertiesV2
  > = useMemo(
    () => ({
      type: 'FeatureCollection',
      features: locations
        .map((geoEvent) => toGeojsonFeature(t, geoEvent, showLocationV2))
        .filter((feature) => feature.properties.opacity > 0),
    }),
    [locations, t, showLocationV2],
  );

  const iconStyle = showLocationV2
    ? getIconStyle(selectedGeoEventId)
    : getLegacyIconStyle(selectedGeoEventId);

  const labelsStyle = showLocationV2
    ? getLabelStyle(selectedGeoEventId)
    : getLegacyLabelStyle(isLightBaseLayer, selectedGeoEventId);

  return (
    <Source id={LOCATIONS_SOURCE_ID} type="geojson" data={geojson}>
      <Layer
        id={LOCATION_ICONS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...iconStyle}
        metadata={{ interactive }}
      />
      <Layer
        id={LOCATION_LABELS_LAYER_ID}
        beforeId={ON_TOP_OF_EVERYTHING_LAYER_ID}
        {...labelsStyle}
        metadata={{ interactive }}
      />
    </Source>
  );
};
